// Importing React, Route, Redirect, connect and PropTypes from the respective libraries
import React from "react";
import { Route, Redirect } from "react-router-dom";
import { connect } from "react-redux";
import PropTypes from "prop-types";

// PrivateRoute component to check if user is authenticated or not
const PrivateRoute = ({ component: Component, auth, ...rest }) => {
  // On a hard reload, redux-persist rehydrates the auth state asynchronously,
  // so `auth.isAuthenticated` is briefly false and the user gets bounced to "/".
  // The jwt token in localStorage is available synchronously (and is already
  // cleared on load if expired — see router.js), so use it as a fallback to
  // avoid that race.
  const hasToken = Boolean(localStorage.getItem("jwtToken"));

  return (
    <Route
      {...rest}
      render={props => {
        // Not logged in → send home.
        if (!(auth.isAuthenticated || hasToken)) {
          return <Redirect to="/" />;
        }

        // Force users who have NOT completed service onboarding into the
        // onboarding flow — they must offer a service before using the app
        // (this is what stops the add-service step from being skipped, e.g.
        // by navigating straight to a private route on mobile). Fail open:
        // only redirect when we positively know onboarding is incomplete, so
        // the rehydration race and legacy users without the flag are never
        // trapped. The /service route itself is exempt to avoid a loop.
        const needsOnboarding =
          auth.isAuthenticated &&
          auth.user &&
          auth.user.isServiceRegisted === false;

        if (needsOnboarding && props.location.pathname !== "/service") {
          return <Redirect to="/service" />;
        }

        return <Component {...props} />;
      }}
    />
  );
};

// Setting propTypes for PrivateRoute component
PrivateRoute.propTypes = {
  auth: PropTypes.object.isRequired
};

// Exporting the connected version of PrivateRoute component
export default connect(state => ({ auth: state.auth }))(PrivateRoute);